import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@nucleus-commerce/ui/lib/utils';

import {
  CORNER_HANDLES,
  croppedSize,
  EDGE_HANDLES,
  frameSize,
  moveCrop,
  resizeCrop,
  type CropHandle,
  type CropRect,
  type ImageEditDraft,
  type Size,
} from '../lib/image-edit';

export type ImageEditTool = 'crop' | 'resize';

export interface ImageEditStageProps {
  src: string;
  /** The preview's own pixels, upright. */
  preview: Size;
  draft: ImageEditDraft;
  tool: ImageEditTool;
  /** The crop's width over height in fractions; null lets it take any shape. */
  ratio: number | null;
  onCropChange: (crop: CropRect) => void;
  onPreviewLoad: (size: Size) => void;
}

const HANDLE_POSITION: Record<CropHandle, string> = {
  nw: '-top-1.5 -left-1.5 cursor-nwse-resize',
  n: '-top-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize',
  ne: '-top-1.5 -right-1.5 cursor-nesw-resize',
  e: 'top-1/2 -right-1.5 -translate-y-1/2 cursor-ew-resize',
  se: '-right-1.5 -bottom-1.5 cursor-nwse-resize',
  s: 'bottom-[-0.375rem] left-1/2 -translate-x-1/2 cursor-ns-resize',
  sw: '-bottom-1.5 -left-1.5 cursor-nesw-resize',
  w: 'top-1/2 -left-1.5 -translate-y-1/2 cursor-ew-resize',
};

function useBoxSize() {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, size };
}

const fitInside = (box: Size, content: Size) =>
  Math.min(box.width / content.width, box.height / content.height);

/** The image turned and mirrored like the saved file will be, filling a box of the frame's shape. */
function TurnedImage({
  src,
  preview,
  draft,
  scale,
  onLoad,
}: {
  src: string;
  preview: Size;
  draft: ImageEditDraft;
  /** Display pixels per preview pixel. */
  scale: number;
  onLoad: (size: Size) => void;
}) {
  const mirrorX = draft.flipHorizontal ? -1 : 1;
  const mirrorY = draft.flipVertical ? -1 : 1;
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      onLoad={(e) =>
        onLoad({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })
      }
      className="absolute top-1/2 left-1/2 max-w-none select-none"
      style={{
        width: preview.width * scale,
        height: preview.height * scale,
        transform: `translate(-50%, -50%) scale(${String(mirrorX)}, ${String(mirrorY)}) rotate(${String(draft.rotate)}deg)`,
      }}
    />
  );
}

function CropBox({
  crop,
  ratio,
  frame,
  onChange,
}: {
  crop: CropRect;
  ratio: number | null;
  /** The frame on screen, to turn a drag in pixels into fractions. */
  frame: Size;
  onChange: (crop: CropRect) => void;
}) {
  const { t } = useTranslation('media');
  const drag = useRef<{
    handle: CropHandle | 'move';
    x: number;
    y: number;
    start: CropRect;
  } | null>(null);

  const begin = (handle: CropHandle | 'move', e: PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { handle, x: e.clientX, y: e.clientY, start: crop };
  };
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const active = drag.current;
    if (!active || frame.width === 0 || frame.height === 0) return;
    const dx = (e.clientX - active.x) / frame.width;
    const dy = (e.clientY - active.y) / frame.height;
    onChange(
      active.handle === 'move'
        ? moveCrop(active.start, dx, dy)
        : resizeCrop(active.start, active.handle, dx, dy, ratio),
    );
  };
  const end = () => {
    drag.current = null;
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.shiftKey ? 0.05 : 0.01;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    onChange(moveCrop(crop, move[0], move[1]));
  };

  const handles = ratio === null ? [...CORNER_HANDLES, ...EDGE_HANDLES] : CORNER_HANDLES;
  return (
    <button
      type="button"
      aria-label={t('editor.cropArea')}
      data-testid="image-crop-box"
      className="focus-visible:ring-ring absolute cursor-move touch-none border border-white shadow-[0_0_0_9999px_rgb(0_0_0/0.55)] outline-none focus-visible:ring-2"
      style={{
        left: `${String(crop.x * 100)}%`,
        top: `${String(crop.y * 100)}%`,
        width: `${String(crop.width * 100)}%`,
        height: `${String(crop.height * 100)}%`,
      }}
      onPointerDown={(e) => begin('move', e)}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
    >
      {/* Rule of thirds, for lining up a subject. */}
      <span className="pointer-events-none absolute inset-y-0 left-1/3 w-1/3 border-x border-white/40" />
      <span className="pointer-events-none absolute inset-x-0 top-1/3 h-1/3 border-y border-white/40" />
      {handles.map((handle) => (
        <span
          key={handle}
          aria-hidden
          className={cn(
            'absolute size-3 touch-none rounded-sm border border-black/30 bg-white',
            HANDLE_POSITION[handle],
          )}
          onPointerDown={(e) => begin(handle, e)}
          onPointerMove={onPointerMove}
          onPointerUp={end}
          onPointerCancel={end}
        />
      ))}
    </button>
  );
}

/**
 * What the saved file will look like, drawn with CSS over the preview so nothing has to be encoded
 * until Save. The crop tool shows the whole turned picture with the crop on it; the resize tool
 * shows the finished shape, with the background around the picture when the size has another shape.
 */
export function ImageEditStage({
  src,
  preview,
  draft,
  tool,
  ratio,
  onCropChange,
  onPreviewLoad,
}: ImageEditStageProps) {
  const { ref, size: box } = useBoxSize();
  const frame = frameSize(preview, draft.rotate);

  if (tool === 'resize' && draft.resize) {
    const cropped = croppedSize(frame, draft.crop);
    const output = fitInside(box, draft.resize);
    const canvas = { width: draft.resize.width * output, height: draft.resize.height * output };
    const toCanvas = fitInside(canvas, cropped);
    const full = { width: frame.width * toCanvas, height: frame.height * toCanvas };
    return (
      <div ref={ref} className="flex size-full items-center justify-center">
        <div
          className="flex shrink-0 items-center justify-center overflow-hidden shadow-sm"
          style={{ ...canvas, backgroundColor: draft.resize.background }}
        >
          <div
            className="relative shrink-0 overflow-hidden"
            style={{
              width: full.width * draft.crop.width,
              height: full.height * draft.crop.height,
            }}
          >
            <div
              className="absolute"
              style={{
                ...full,
                left: -draft.crop.x * full.width,
                top: -draft.crop.y * full.height,
              }}
            >
              <TurnedImage
                src={src}
                preview={preview}
                draft={draft}
                scale={toCanvas}
                onLoad={onPreviewLoad}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const scale = fitInside(box, frame);
  const shown = { width: frame.width * scale, height: frame.height * scale };
  return (
    <div ref={ref} className="flex size-full items-center justify-center">
      <div className="relative shrink-0 overflow-hidden" style={shown}>
        <TurnedImage
          src={src}
          preview={preview}
          draft={draft}
          scale={scale}
          onLoad={onPreviewLoad}
        />
        <CropBox crop={draft.crop} ratio={ratio} frame={shown} onChange={onCropChange} />
      </div>
    </div>
  );
}
